/**
 * ParkMap.jsx — the hub of Prayogashaala Physics Park.
 * Each phase is INDEPENDENT — students pick based on their own knowledge gaps.
 * The only soft lock: you should ride a phase before taking its own exam.
 */
import { useState } from "react";
import { useGame } from "./store";
import Mentor from "./Mentor";
import { initAudio } from "./sound";

const FUTURE_LANDS = [
  { icon: "🪂", name: "Splash World", topics: "Projectile motion · air resistance · terminal velocity · buoyancy · viscosity", tease: "The Sky Drop tower and Log Flume — where falling is the whole point." },
  { icon: "🎢", name: "Oscillation Oasis", topics: "Tension · springs · SHM · pendulums", tease: "The Giant Swing's cable has a tension rating. Exceed it… once." },
  { icon: "💥", name: "Collision Coliseum", topics: "Momentum · impulse · collisions", tease: "Bumper carts where every crash must be predicted before it happens." },
  { icon: "🌪", name: "Spin City", topics: "Torque · moment of inertia · angular momentum · rolling", tease: "Ring vs disc vs sphere — the race that surprises everyone." },
];

export default function ParkMap() {
  const closeMap = useGame((s) => s.closeMap);
  const startGame = useGame((s) => s.startGame);
  const openExam = useGame((s) => s.openExam);
  const progress = useGame((s) => s.progress);

  const p1Cert = progress.p1Lap && progress.p1Exam;

  // Message shown when a locked button is clicked (only "ride first" soft-lock now)
  const [lockNotice, setLockNotice] = useState(null);

  const ride = () => {
    initAudio();
    startGame();
    closeMap();
  };

  // Mentor adapts to overall progress
  const mentorLine = p1Cert
    ? "Circular Motion certified — Engineer! Splash World is under construction. More modules coming soon. Challenge mode records are waiting to be broken."
    : "Welcome to Prayogashaala Physics Park! Every ride here obeys Newton. Ride first, then take the exam to earn your Circular Motion stamp.";

  const stampStyle = (done) => ({
    fontSize: 11,
    fontWeight: 800,
    padding: "3px 10px",
    borderRadius: 100,
    background: done ? "rgba(0,255,170,0.15)" : "rgba(255,255,255,0.06)",
    border: `1px solid ${done ? "rgba(0,255,170,0.5)" : "rgba(255,255,255,0.15)"}`,
    color: done ? "#00ffaa" : "#5a6478",
  });

  // Phase 1 card
  const lapDone = progress.p1Lap;
  const examDone = progress.p1Exam;

  return (
    <div
      data-testid="park-map"
      style={{
        position: "fixed", inset: 0, zIndex: 44,
        background: "radial-gradient(ellipse at top, #1a1d3a 0%, #07080f 60%, #02030a 100%)",
        overflowY: "auto",
        color: "#fff", fontFamily: "'Inter', system-ui, sans-serif",
        padding: "30px 16px 60px",
      }}
    >
      <div style={{ width: "min(880px, 100%)", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 4, color: "#ffd23f", fontWeight: 700 }}>🗺 PARK MAP</div>
            <h1 style={{ margin: "4px 0", fontSize: 32, fontWeight: 900 }}>Prayogashaala Physics Park</h1>
            <div style={{ fontSize: 12, color: "#9aa4b2", marginTop: 2 }}>
              Each module is independent — pick what matches your syllabus
            </div>
          </div>
          <button
            onClick={closeMap}
            style={{ padding: "10px 18px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer" }}
          >
            ← Menu
          </button>
        </div>

        {/* Engineer's License */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "10px 0 6px", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, letterSpacing: 2, color: "#9aa4b2", fontWeight: 700 }}>🪪 ENGINEER'S LICENSE:</span>
          <span style={stampStyle(p1Cert)}>{p1Cert ? "✓" : "○"} CIRCULAR MOTION</span>
          <span style={stampStyle(false)}>○ PROJECTILE & FLUIDS</span>
          <span style={stampStyle(false)}>○ OSCILLATIONS</span>
          <span style={stampStyle(false)}>○ MOMENTUM</span>
          <span style={stampStyle(false)}>○ ROTATION</span>
        </div>

        <Mentor text={mentorLine} />

        {/* Why-locked notice (only for exam-before-ride) */}
        {lockNotice && (
          <Mentor mood="alert" compact text={lockNotice} />
        )}

        {/* ── PHASE 1 — Circular Motion ── */}
        <div style={{
          background: "rgba(10,12,24,0.85)",
          border: `1px solid ${p1Cert ? "rgba(0,255,170,0.4)" : "rgba(255,255,255,0.14)"}`,
          borderRadius: 16, padding: "20px 22px", marginBottom: 16,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: "#3eb6ff", fontWeight: 800, letterSpacing: 1.5 }}>
                PHASE 1 — Circular Motion {p1Cert ? "· ✓ CERTIFIED" : "· OPEN"}
              </div>
              <div style={{ fontSize: 21, fontWeight: 900 }}>🌀 Circular Motion Coaster</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <span style={stampStyle(lapDone)}>{lapDone ? "✓" : "○"} RIDE</span>
              <span style={stampStyle(examDone)}>{examDone ? "✓" : "○"} EXAM</span>
            </div>
          </div>
          <p style={{ color: "#cfd6e3", fontSize: 13.5, lineHeight: 1.6, margin: "10px 0" }}>
            Six curves, three scenario checkpoints: reason → choose the equation → compute v_max on banked curves, incline slopes, and air-drag descents — then DRIVE it. Learn why curves have speed limits, why banking angle matters, and why drag caps your terminal speed.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={ride}
              style={{ flex: 1, minWidth: 140, padding: "12px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#00ffaa,#00b8a9)", color: "#001a14", fontWeight: 800, fontSize: 14, cursor: "pointer" }}
            >
              ▶ RIDE
            </button>
            <button
              onClick={() =>
                lapDone
                  ? openExam()
                  : setLockNotice("Ride the Circular Motion Coaster at least once before taking its exam — the questions test what you experience on the track.")
              }
              style={{ flex: 1, minWidth: 140, padding: "12px", borderRadius: 10, border: "none", background: lapDone ? "linear-gradient(135deg,#3eb6ff,#e879f9)" : "rgba(255,255,255,0.06)", color: lapDone ? "#0a0c1e" : "#5a6478", fontWeight: 800, fontSize: 14, cursor: "pointer" }}
            >
              📝 EXAM {lapDone ? "" : "🔒"}
            </button>
          </div>
        </div>

        {/* ── FUTURE LANDS ── */}
        <div style={{ fontSize: 12, letterSpacing: 2, color: "#9aa4b2", fontWeight: 700, margin: "18px 0 10px" }}>
          🏗 UNDER CONSTRUCTION — COMING PHASES
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          {FUTURE_LANDS.map((l) => (
            <div key={l.name} style={{ background: "rgba(10,12,24,0.6)", border: "1px dashed rgba(255,255,255,0.18)", borderRadius: 14, padding: "14px 16px", opacity: 0.8 }}>
              <div style={{ fontSize: 22 }}>{l.icon}</div>
              <div style={{ fontSize: 15, fontWeight: 800, margin: "4px 0" }}>{l.name}</div>
              <div style={{ fontSize: 11.5, color: "#3eb6ff", marginBottom: 6 }}>{l.topics}</div>
              <div style={{ fontSize: 12, color: "#9aa4b2", lineHeight: 1.5 }}>{l.tease}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
